/**
 * Normalizes errors from either of this app's two API-calling mechanisms:
 *  - the generated OpenAPI client (`src/api/`), which throws a bare `Response`
 *    object on non-2xx responses (see `src/api/runtime.ts`, `throw response;`
 *    — there is no `ResponseError` wrapper class in this generator version).
 *  - the raw axios instance (`src/store/axiosContext.tsx`), whose errors
 *    carry the message at `error.response.data.message`.
 * Also handles plain `Error` instances and falls back to a caller-supplied
 * message for anything else.
 */

interface IAxiosLikeError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

function isResponse(error: unknown): error is Response {
  return typeof Response !== 'undefined' && error instanceof Response;
}

function isAxiosLikeError(error: unknown): error is IAxiosLikeError {
  return typeof error === 'object' && error !== null && 'response' in error;
}

export async function getApiErrorMessage(error: unknown, fallback: string): Promise<string> {
  if (isResponse(error)) {
    try {
      // Always clone first — the body may already have been consumed
      // elsewhere (e.g. by the 401-handling middleware in apiContext.tsx).
      const body = await error.clone().json();
      if (body && typeof body.message === 'string' && body.message) {
        return body.message;
      }
    } catch {
      // body missing / not JSON / already consumed and unparseable — fall through
    }
    return `Chyba serveru (${error.status})`;
  }

  if (isAxiosLikeError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message) {
      return message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export function getApiErrorStatus(error: unknown): number | undefined {
  if (isResponse(error)) {
    return error.status;
  }

  if (isAxiosLikeError(error)) {
    return error.response?.status;
  }

  return undefined;
}
